/**
 * Theme registry and types for modular color themes.
 * Add new themes by adding an entry to THEMES and a [data-theme="..."] block in base.css.
 * Use 'custom' for user-created palettes (applied at runtime from localStorage).
 */

export type ThemeId = string;

export interface ThemeDescriptor {
  id: ThemeId;
  label: string;
  isDark: boolean;
}

/** CSS variable names used for semantic theming (custom palette modal and apply logic). */
export const THEME_VARIABLE_NAMES = [
  'background',
  'foreground',
  'muted',
  'muted-foreground',
  'popover',
  'popover-foreground',
  'border',
  'input',
  'input-foreground',
  'input-background',
  'input-border',
  'card',
  'card-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'ring',
  'sidebar-background',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
  'view-pane-background',
] as const;

export type ThemeVariableName = (typeof THEME_VARIABLE_NAMES)[number];

/** Map of CSS variable name (without --) to HSL value string, e.g. "220 14% 11%" */
export type CustomPalette = Partial<Record<ThemeVariableName, string>>;

export const CUSTOM_PALETTE_STORAGE_KEY = 'truss-custom-palette';

export const THEMES: readonly ThemeDescriptor[] = [
  { id: 'light', label: 'Light', isDark: false },
  { id: 'dark-charcoal', label: 'Dark', isDark: true },
  { id: 'light-warm', label: 'Light Warm', isDark: false },
  { id: 'light-cool', label: 'Light Cool', isDark: false },
  { id: 'light-high-contrast', label: 'Light High Contrast', isDark: false },
  { id: 'dark-blue', label: 'Dark Blue', isDark: true },
  { id: 'dark-oled', label: 'Dark OLED', isDark: true },
  { id: 'dark-warm', label: 'Dark Warm', isDark: true },
  { id: 'dark-midnight', label: 'Dark Midnight', isDark: true },
  { id: 'dark-forest', label: 'Dark Forest', isDark: true },
] as const;

export const PRESET_THEME_IDS = THEMES.map((t) => t.id);

export const CUSTOM_THEME_ID: ThemeId = 'custom';

const themeMap = new Map<ThemeId, ThemeDescriptor>(
  THEMES.map((t) => [t.id, t])
);

export function getTheme(id: ThemeId): ThemeDescriptor | null {
  if (id === CUSTOM_THEME_ID) {
    return { id: CUSTOM_THEME_ID, label: 'My palette', isDark: false };
  }
  return themeMap.get(id) ?? null;
}

export function isDarkTheme(id: ThemeId): boolean {
  const theme = getTheme(id);
  return theme?.isDark ?? false;
}

/**
 * Infer whether a custom palette is dark-toned by reading the lightness component
 * of its `--background` HSL string (format: "H S% L%"). Returns true when L <= 50%.
 * Falls back to false when no palette is provided or parsing fails.
 */
export function isCustomPaletteDark(palette: CustomPalette | null): boolean {
  if (!palette) return false;
  const bg = palette.background;
  if (typeof bg !== 'string') return false;
  const parts = bg.trim().split(/\s+/);
  const lightnessRaw = parts[2];
  if (!lightnessRaw) return false;
  const lightness = parseFloat(lightnessRaw.replace('%', ''));
  if (!Number.isFinite(lightness)) return false;
  return lightness <= 50;
}

const DEFAULT_DARK_THEME_ID: ThemeId = 'dark-charcoal';

export function getDefaultDarkTheme(): ThemeId {
  return DEFAULT_DARK_THEME_ID;
}

export function getInitialTheme(): ThemeId {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? DEFAULT_DARK_THEME_ID
    : 'light';
}

export function getCustomPalette(): CustomPalette | null {
  if (typeof localStorage === 'undefined') return null;
  const raw = localStorage.getItem(CUSTOM_PALETTE_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as CustomPalette;
  } catch {
    return null;
  }
}

export function setCustomPalette(palette: CustomPalette): void {
  localStorage.setItem(CUSTOM_PALETTE_STORAGE_KEY, JSON.stringify(palette));
}

/** Apply custom palette variables to document root. Call when theme is 'custom'. */
export function applyCustomTheme(palette: CustomPalette): void {
  const root = document.documentElement;
  THEME_VARIABLE_NAMES.forEach((name) => {
    const value = palette[name];
    if (value != null) {
      root.style.setProperty(`--${name}`, value);
    }
  });
}

/** Clear custom theme variables from document root (so CSS blocks take over again). */
export function clearCustomThemeFromDocument(): void {
  const root = document.documentElement;
  THEME_VARIABLE_NAMES.forEach((name) => {
    root.style.removeProperty(`--${name}`);
  });
}

/** Default light theme values for custom palette modal when no palette saved yet. */
export const DEFAULT_LIGHT_PALETTE: CustomPalette = {
  background: '0 0% 100%',
  foreground: '222.2 47.4% 11.2%',
  muted: '210 40% 96.1%',
  'muted-foreground': '215.4 16.3% 46.9%',
  popover: '0 0% 100%',
  'popover-foreground': '222.2 47.4% 11.2%',
  border: '214.3 31.8% 91.4%',
  input: '214.3 31.8% 91.4%',
  'input-foreground': '222.2 47.4% 11.2%',
  'input-background': '0 0% 100%',
  'input-border': '214.3 31.8% 91.4%',
  card: '0 0% 100%',
  'card-foreground': '222.2 47.4% 11.2%',
  primary: '222.2 47.4% 11.2%',
  'primary-foreground': '210 40% 98%',
  secondary: '210 40% 96.1%',
  'secondary-foreground': '222.2 47.4% 11.2%',
  accent: '210 40% 96.1%',
  'accent-foreground': '222.2 47.4% 11.2%',
  destructive: '0 100% 50%',
  'destructive-foreground': '210 40% 98%',
  ring: '215 20.2% 65.1%',
  'sidebar-background': '0 0% 98%',
  'sidebar-foreground': '240 5.3% 26.1%',
  'sidebar-primary': '240 5.9% 10%',
  'sidebar-primary-foreground': '0 0% 98%',
  'sidebar-accent': '240 4.8% 95.9%',
  'sidebar-accent-foreground': '240 5.9% 10%',
  'sidebar-border': '220 13% 91%',
  'sidebar-ring': '217.2 91.2% 59.8%',
};
